import React, { useEffect, useMemo, useState } from "react";
import Layout from "../layout/index";
import axios from "axios";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { ROOT_API } from "../../api/config";
import { formatDate } from "../../utils/dates";
import { isPremiumUser } from "../../utils/premium";
import "./style.scss";

const fold = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

// Broad bucket for an outage = the first comma-component of its location (the distribution
// area for electricity, the city for water), so the page groups instead of one flat list.
const regionOf = (o) => ((o.location || "").split(",")[0] || "").trim() || "—";

const Today = () => {
  const intl = useIntl();
  const selectedLocale =
    useSelector((state) => state.lang.value) ||
    localStorage.getItem("lang") ||
    "hr";
  const locale = selectedLocale === "en" ? "en-US" : "hr-HR";
  const isPremium = isPremiumUser(useSelector((state) => state.user));

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [query, setQuery] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all"); // all | electricity | water

  useEffect(() => {
    let alive = true;
    (async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const res = await axios.get(`${ROOT_API}apis/today-outages`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        if (alive) setData(res?.data?.data || null);
      } catch (e) {
        if (alive) setHasError(true);
      } finally {
        if (alive) setIsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const outages = data?.outages || [];
  const svc = (o) => (o.service === "water" ? "water" : "electricity");
  const counts = useMemo(() => {
    let e = 0;
    let w = 0;
    outages.forEach((o) => (svc(o) === "water" ? (w += 1) : (e += 1)));
    return { all: outages.length, electricity: e, water: w };
  }, [outages]);

  // Filter by search + service, then group by region (sorted, each group's items kept).
  const groups = useMemo(() => {
    const q = fold(query.trim());
    const filtered = outages.filter((o) => {
      if (serviceFilter !== "all" && svc(o) !== serviceFilter) return false;
      if (!q) return true;
      return (
        fold(o.street_raw).includes(q) ||
        fold(o.street).includes(q) ||
        fold(o.location).includes(q)
      );
    });
    const byRegion = new Map();
    filtered.forEach((o) => {
      const r = regionOf(o);
      if (!byRegion.has(r)) byRegion.set(r, []);
      byRegion.get(r).push(o);
    });
    return [...byRegion.entries()].sort((a, b) => a[0].localeCompare(b[0], "hr"));
  }, [outages, query, serviceFilter]);

  const totalShown = groups.reduce((n, [, items]) => n + items.length, 0);

  const TABS = [
    { id: "all", label: intl.formatMessage({ id: "today_filter_all" }), n: counts.all },
    { id: "electricity", label: `⚡ ${intl.formatMessage({ id: "service_tag_electricity" })}`, n: counts.electricity },
    { id: "water", label: `💧 ${intl.formatMessage({ id: "service_tag_water" })}`, n: counts.water },
  ];

  return (
    <Layout>
      <div className="today">
        <div className="container">
          <Link to="/pocetna" className="today__back">
            ← {intl.formatMessage({ id: "today_back" })}
          </Link>

          <section className="today__hero">
            <p className="today__eyebrow">
              {intl.formatMessage({ id: "today_eyebrow" })}
            </p>
            <h1 className="today__title">
              {intl.formatMessage({ id: "today_title" })}
            </h1>
            <p className="today__lead">
              {data?.date
                ? intl.formatMessage(
                    { id: "today_subtitle" },
                    { date: formatDate(data.date, locale) }
                  )
                : intl.formatMessage({ id: "today_subtitle_plain" })}
            </p>
          </section>

          {!isPremium ? (
            <div className="today__upsell">
              <span>{intl.formatMessage({ id: "today_upsell" })}</span>
              <Link to="/pretplata" className="today__upsell-cta">
                {intl.formatMessage({ id: "today_upsell_cta" })}
              </Link>
            </div>
          ) : null}

          <div className="today__controls">
            <div className="today__tabs">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`today__tab${serviceFilter === t.id ? " is-active" : ""}`}
                  onClick={() => setServiceFilter(t.id)}
                >
                  {t.label} <span className="today__tab-count">{t.n}</span>
                </button>
              ))}
            </div>
            <input
              className="today__search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={intl.formatMessage({ id: "today_search_placeholder" })}
            />
          </div>

          {isLoading ? (
            <div className="today__state">
              {intl.formatMessage({ id: "today_loading" })}
            </div>
          ) : hasError ? (
            <div className="today__state today__state--error">
              {intl.formatMessage({ id: "today_error" })}
            </div>
          ) : totalShown === 0 ? (
            <div className="today__state">
              {intl.formatMessage({
                id: outages.length === 0 ? "today_none" : "today_no_match",
              })}
            </div>
          ) : (
            <div className="today__groups">
              {groups.map(([region, items]) => (
                <section className="today__group" key={region}>
                  <header className="today__group-head">
                    <h2 className="today__group-title">{region}</h2>
                    <span className="today__group-count">{items.length}</span>
                  </header>
                  <div className="today__list">
                    {items.map((o, i) => (
                      <article className="today__card" key={`${region}-${o.street}-${i}`}>
                        <span
                          className={`today__svc today__svc--${svc(o)}`}
                          title={intl.formatMessage({
                            id: svc(o) === "water" ? "service_tag_water" : "service_tag_electricity",
                          })}
                        >
                          {svc(o) === "water" ? "💧" : "⚡"}
                        </span>
                        <div className="today__card-main">
                          <p className="today__card-street">{o.street_raw || o.street}</p>
                          {o.note ? <p className="today__card-note">{o.note}</p> : null}
                        </div>
                        {o.duration ? (
                          <span className="today__card-time">{o.duration}</span>
                        ) : null}
                      </article>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Today;
